// Copyright 2017 Sorama B.V.

import { Point2D, Point3D } from "datatypes/SoramaLocalApi";

/**
 *  Uniform Linear Array (ULA) properties
 */
export class ULAProperties {
    public isUla: boolean;
    public length: number;
    public spacing: number;
    public directionVector: Point2D;

    public get lengthInElements(): number {
        if (!this.length || !this.spacing) return undefined;

        return this.length / this.spacing;
    }

    constructor(isUla: boolean, length?: number, spacing?: number, directionVector?: Point2D) {
        this.isUla = isUla;
        this.length = length;
        this.spacing = spacing;
        this.directionVector = directionVector;
    }
}

/**
 *  Uniform Rectangular Array (URA)
 */
export class URAProperties {
    public isUra: boolean;
    /** If the points are on a URA grid, but possible not all grid points are represented (isURA=true will always mean isURASubset=true)
     * We require at least 2 points, which are not on the same x or y coordinate
     */
    public isUraSubset: boolean;
    /**
     * This is the distance between the most left and most right microphone, PLUS one spacing
     */
    public width: number;
    /**
     * This is the distance between the most top and most bottom microphone, PLUS one spacing
     */
    public height: number;
    public spacing: number;

    public get widthInElements(): number {
        return Math.round(this.width / this.spacing);
    }

    public get heightInElements(): number {
        return Math.round(this.height / this.spacing);
    }

    constructor(isUra: boolean, isUraSubset: boolean, width?: number, height?: number, spacing?: number) {
        this.isUra = isUra;
        this.isUraSubset = isUraSubset;
        this.width = width;
        this.height = height;
        this.spacing = spacing;
    }
}

export class URA {
    // Allow microphones to be shifted out of alignment by 100 micrometers (0.1 mm)
    private static readonly positionalError = 1e-4;

    /**
     *
     * @param points Get the properties of a  Uniform Linear Array (ULA)
     * @param roundPropertiesToDecimal
     */
    public static getUlaProperties(points: Point2D[], roundPropertiesToDecimal = 3): ULAProperties {
        // First check the trivial cases
        if (!points || points.length <= 1) {
            // No points or 1 point, not an ULA
            return new ULAProperties(false);
        }

        // Map all points so that the 1st one is on the origin
        const movedPoints = points.map((x) => <Point2D>{ x: x.x - points[0].x, y: x.y - points[0].y });

        // Assume all points are on a line, get the normalized line vector from the 2nd point
        let length = Math.sqrt(movedPoints[1].x * movedPoints[1].x + movedPoints[1].y * movedPoints[1].y);
        const dirVect = <Point2D>{ x: movedPoints[1].x / length, y: movedPoints[1].y / length };

        if (dirVect.x === 0 && dirVect.y === 0) {
            // Atleast 2 points on exactly the same coordinate, not an ULA
            return new ULAProperties(false);
        }

        // We have an ULA if all points are a multiple of the direction vector
        let spacing = length;
        for (let i = 2; i < movedPoints.length; i++) {
            const point = movedPoints[i];
            const nx = point.x / dirVect.x;
            const ny = point.y / dirVect.y;

            // nx or ny can be NaN if dirVect has a 0 component
            const n = Number.isNaN(nx) ? ny : nx;

            // Point should be on a line at distance n from the origin

            // n is equal to the ULA spacing if it is the smallest distance found in the entire array.
            if (Math.abs(n) < spacing) spacing = Math.abs(n);

            // n is equal to the ULA length if it is the largest distance found in the entire array.
            if (Math.abs(n) > length) length = Math.abs(n);
        }

        if (spacing * (movedPoints.length - 1) !== length) {
            // The math doesn't add up (2 points on the same location for instance), not an ULA
            return new ULAProperties(false);
        }

        // Update length to complete aperture size
        length += spacing;

        const roundingFactor = Math.pow(10, roundPropertiesToDecimal);
        length = Math.round(roundingFactor * length) / roundingFactor;
        spacing = Math.round(roundingFactor * spacing) / roundingFactor;

        return new ULAProperties(true, length, spacing, dirVect);
    }

    public static getUraProperties3D(points: Point3D[], roundPropertiesToDecimal = 3): URAProperties {
        if (points.length > 0 && !points.every((x) => x.z === points[0].z)) {
            throw new Error("Only 2D configurations are supported at the moment. All z values should be the same.");
        }

        return URA.getUraProperties(points, roundPropertiesToDecimal);
    }

    /**
     * Checks if a collection of 2D points is an Uniform Rectangular Array (URA) and returns the properties of this URA (if any).
     * @param points The points to check.
     * @param roundPropertiesToDecimal Round the URA properties to this much decimals (default: 3, assumption here is that input is in [m] and we round to the nearest [mm])
     */
    public static getUraProperties(points: Point2D[], roundPropertiesToDecimal = 3): URAProperties {
        // First check the trivial cases
        if (!points || points.length <= 1) {
            // No points or 1 point, not a URA
            return new URAProperties(false, false, 0, 0, 0);
        }

        // sort all microphones left to right, top to bottom
        points = [...points].sort((a, b) => a.y - b.y || a.x - b.x);

        // Find smallest distance in x and y direction
        let spacingX = Number.POSITIVE_INFINITY;
        let spacingY = Number.POSITIVE_INFINITY;
        for (let x = 0; x < points.length; x++) {
            for (let y = x + 1; y < points.length; y++) {
                if (x === y) continue;

                const dx = Math.abs(points[y].x - points[x].x);
                const dy = Math.abs(points[y].y - points[x].y);

                if (dx === 0 && dy === 0) {
                    // Atleast 2 points on exactly the same coordinate, not an URA
                    return new URAProperties(false, false, 0, 0, 0);
                }

                if (dx > URA.positionalError && dx < spacingX) spacingX = dx;
                if (dy > URA.positionalError && dy < spacingY) spacingY = dy;
            }
        }

        let spacing = Math.min(spacingX, spacingY);

        // If it's a URASubset, we can see this by check whether all points are at a factor of the spacing away from one point
        // We can use the first point as a reference point
        const referencePoint = points[0];

        for (let i = 1; i < points.length; i++) {
            const dx = Math.abs(points[i].x - referencePoint.x);
            const dy = Math.abs(points[i].y - referencePoint.y);

            if (dx % spacing > URA.positionalError || dy % spacing > URA.positionalError) {
                // Not a URA
                return new URAProperties(false, false, 0, 0, 0);
            }
        }

        // We know know it's at least a URASubset, since all points are on the grid
        const isURASubset = true;

        // Now to see if it's a full URA, we see if, given width, height and spacing, we have the correct number of microphones
        const minX = Math.min(...points.map((p) => p.x));
        const minY = Math.min(...points.map((p) => p.y));
        const maxX = Math.max(...points.map((p) => p.x));
        const maxY = Math.max(...points.map((p) => p.y));

        let width = maxX - minX;
        let height = maxY - minY;

        if (width === 0 || height === 0) {
            // Not a URA but a ULA
            return new URAProperties(false, false, 0, 0, 0);
        }

        // Update width and height to complete aperture size
        width += spacing;
        height += spacing;
        const widthInElements = Math.round(width / spacing);
        const heightInElements = Math.round(height / spacing);

        let isFullURA = false;
        if (widthInElements * heightInElements == points.length) {
            isFullURA = true;
        }

        // Round the properties to the desired number of decimals
        const roundingFactor = Math.pow(10, roundPropertiesToDecimal);
        width = Math.round(roundingFactor * width) / roundingFactor;
        height = Math.round(roundingFactor * height) / roundingFactor;
        spacing = Math.round(roundingFactor * spacing) / roundingFactor;

        return new URAProperties(isFullURA, isURASubset, width, height, spacing);
    }
}
